import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../services/api';
import './CategoryTree.css';

const CategoryTree = ({ 
  onCategorySelect = null,
  showIcons = true,
  showCounts = true,
  expandAll = false,
  className = ''
}) => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  useEffect(() => {
    fetchCategoryTree();
  }, []);

  useEffect(() => {
    if (expandAll && tree.length > 0) {
      const allNodeIds = new Set();
      const collectNodeIds = (nodes) => {
        nodes.forEach(node => {
          allNodeIds.add(node.id);
          if (node.children && node.children.length > 0) {
            collectNodeIds(node.children);
          }
        });
      };
      collectNodeIds(tree);
      setExpandedNodes(allNodeIds);
    }
  }, [expandAll, tree]);

  const fetchCategoryTree = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await categoriesAPI.getCategoryTree();

      if (response.success) {
        setTree(response.data);
      } else {
        setError(response.message || 'Failed to fetch category tree');
      }
    } catch (err) {
      console.error('Error fetching category tree:', err);
      setError('Failed to load category tree. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleCategoryClick = (category) => {
    if (onCategorySelect) {
      onCategorySelect(category);
    }
  };

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const indentStyle = { paddingLeft: `${level * 20}px` };

    return (
      <div key={node.id} className="tree-node">
        <div 
          className={`tree-node-content ${hasChildren ? 'has-children' : ''} ${isExpanded ? 'expanded' : ''}`}
          style={indentStyle}
        >
          {hasChildren && (
            <button
              className="tree-toggle"
              onClick={() => toggleNode(node.id)}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
            </button>
          )}
          
          {!hasChildren && <div className="tree-spacer"></div>}

          <div 
            className="tree-node-info"
            onClick={() => handleCategoryClick(node)}
          >
            {showIcons && (
              <div className="tree-node-icon">
                {node.icon ? (
                  <i className={`${node.icon} tree-icon-svg`}></i>
                ) : (
                  <i className="bi bi-tag tree-icon-svg"></i>
                )}
              </div>
            )}

            <div className="tree-node-details">
              <span className="tree-node-name">{node.name}</span>
              {node.description && (
                <span className="tree-node-description">{node.description}</span>
              )}
            </div>

            {showCounts && hasChildren && (
              <div className="tree-node-count">
                <span className="count-badge">{node.children.length}</span>
              </div>
            )}

            {node.is_featured && (
              <div className="tree-node-featured">
                <i className="bi bi-star-fill"></i>
              </div>
            )}
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="tree-children">
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderLoading = () => (
    <div className="category-tree-loading">
      <div className="loading-spinner"></div>
      <p>Loading category tree...</p>
    </div>
  );

  const renderError = () => (
    <div className="category-tree-error">
      <i className="bi bi-exclamation-triangle"></i>
      <p>{error}</p>
      <button onClick={fetchCategoryTree} className="retry-button">
        Try Again
      </button>
    </div>
  );

  const renderEmpty = () => (
    <div className="category-tree-empty">
      <i className="bi bi-diagram-3"></i>
      <h3>No categories found</h3>
      <p>There are no categories available at the moment.</p>
    </div>
  );

  if (loading) return renderLoading();
  if (error) return renderError();
  if (tree.length === 0) return renderEmpty();

  return (
    <div className={`category-tree ${className}`}>
      <div className="tree-header">
        <h3>Category Tree</h3>
        <div className="tree-controls">
          <button 
            className="tree-control-btn"
            onClick={() => setExpandedNodes(new Set())}
            title="Collapse All"
          >
            <i className="bi bi-chevron-up"></i>
          </button>
          <button 
            className="tree-control-btn"
            onClick={() => {
              const allNodeIds = new Set();
              const collectNodeIds = (nodes) => {
                nodes.forEach(node => {
                  allNodeIds.add(node.id);
                  if (node.children && node.children.length > 0) {
                    collectNodeIds(node.children);
                  }
                });
              };
              collectNodeIds(tree);
              setExpandedNodes(allNodeIds);
            }}
            title="Expand All"
          >
            <i className="bi bi-chevron-down"></i>
          </button>
        </div>
      </div>
      
      <div className="tree-content">
        {tree.map(node => renderTreeNode(node))}
      </div>
    </div>
  );
};

export default CategoryTree;
